import { Component, type ErrorInfo, type ReactNode } from 'react'

import { clientLogger } from '@/services/telemetry'

import { ErrorState } from './error-state'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Rendered instead of the default screen. Receives a reset callback. */
  fallback?: (state: { error: Error; reference: string; reset: () => void }) => ReactNode
  /** Identifies which part of the tree failed, recorded with the log entry. */
  boundary?: string
  onError?: (error: Error, info: ErrorInfo) => void
}

interface ErrorBoundaryState {
  error: Error | undefined
  reference: string
}

/**
 * Catches render/lifecycle exceptions below it. React does not route event
 * handler, async, or SSR errors here, so those still need local handling.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: undefined, reference: '' }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[MasterHub ErrorBoundary]', error, info.componentStack)

    const reference = clientLogger.error('Unhandled React error', error, {
      boundary: this.props.boundary ?? 'root',
      componentStack: info.componentStack ?? undefined,
    })

    this.setState({ reference })
    this.props.onError?.(error, info)
  }

  private reset = (): void => {
    this.setState({ error: undefined, reference: '' })
  }

  render(): ReactNode {
    const { error, reference } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) {
      return this.props.fallback({ error, reference, reset: this.reset })
    }

    return (
      <ErrorState
        title="Algo deu errado"
        description="Encontramos um erro inesperado ao carregar esta área. Você pode tentar novamente ou voltar para o início."
        error={error}
        reference={reference}
        onRetry={this.reset}
      />
    )
  }
}
